import React from "react";
import { Box, Container, Typography, Button, Chip } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BRAND from "../theme/talktokam";

const webinars = [
  {
    title: "Getting Started with AI Agents",
    desc: "Learn how to deploy pre-built AI personas for sales, support, lead qualification, and more.",
    date: "Apr 10, 2025",
    time: "11:00 AM IST",
    host: "Product Team",
    status: "upcoming",
  },
  {
    title: "Advanced Flow Builder Techniques",
    desc: "Deep dive into conditions, jumps, variables, and complex conversation flows.",
    date: "Apr 24, 2025",
    time: "2:00 PM IST",
    host: "Engineering Team",
    status: "upcoming",
  },
  {
    title: "From 0 to 10K Conversations: Growth Playbook",
    desc: "Strategies for scaling your chatbot from zero to thousands of conversations.",
    date: "Mar 20, 2025",
    time: "3:00 PM IST",
    host: "Customer Success",
    status: "recorded",
  },
  {
    title: "WhatsApp for Business: Setup Guide",
    desc: "Step-by-step walkthrough of WhatsApp Business API integration with Talktokam.",
    date: "Mar 5, 2025",
    time: "1:00 PM IST",
    host: "Product Team",
    status: "recorded",
  },
  {
    title: "Live Chat Best Practices",
    desc: "How to combine AI chatbots with human live agents for the perfect customer experience.",
    date: "Feb 15, 2025",
    time: "11:00 AM IST",
    host: "Customer Success",
    status: "recorded",
  },
];

const Webinars = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              Master Talktokam with Live Webinars
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#94a3b8",
                maxWidth: 550,
                mx: "auto",
              }}
            >
              Join our product experts for deep dives, best practices, and Q&A
              sessions.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{ background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)" }}
      >
        <Container sx={{ py: { xs: 6, md: 10 }, maxWidth: 900 }}>
          {webinars.map((w, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: 4,
                  bgcolor: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  mb: 2,
                  display: "flex",
                  gap: 3,
                  alignItems: "center",
                  flexWrap: "wrap",
                  transition: "all 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(56,189,248,0.3)",
                    boxShadow: "0 12px 40px rgba(56,189,248,0.1)",
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}
                    >
                      {w.title}
                    </Typography>
                    <Chip
                      label={w.status === "upcoming" ? "Live" : "Recorded"}
                      size="small"
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        bgcolor:
                          w.status === "upcoming"
                            ? "rgba(22,163,74,0.15)"
                            : "rgba(255,255,255,0.08)",
                        color: w.status === "upcoming" ? "#16a34a" : "#94a3b8",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.85rem", color: "#94a3b8", mb: 1 }}
                  >
                    {w.desc}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>
                    {w.date} · {w.time} · {w.host}
                  </Typography>
                </Box>
                <Button
                  variant={w.status === "upcoming" ? "contained" : "outlined"}
                  size="small"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    flexShrink: 0,
                    background:
                      w.status === "upcoming"
                        ? "linear-gradient(135deg, #4F46E5, #7C3AED)"
                        : "none",
                    borderColor: "rgba(99,102,241,0.5)",
                    color: w.status === "upcoming" ? "#fff" : "#6366f1",
                    boxShadow:
                      w.status === "upcoming"
                        ? "0 4px 15px rgba(79,70,229,0.4)"
                        : "none",
                    "&:hover":
                      w.status === "upcoming"
                        ? { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" }
                        : {
                            borderColor: "#6366f1",
                            bgcolor: "rgba(99,102,241,0.1)",
                          },
                  }}
                >
                  {w.status === "upcoming" ? "Register Now" : "Watch Recording"}
                </Button>
              </Box>
            </motion.div>
          ))}

          <Box
            sx={{
              mt: 6,
              textAlign: "center",
              p: 4,
              borderRadius: 4,
              bgcolor: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.08)",
              transition: "all 0.3s",
              "&:hover": {
                transform: "translateY(-4px)",
                borderColor: "rgba(56,189,248,0.3)",
                boxShadow: "0 12px 40px rgba(56,189,248,0.1)",
              },
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#fff", mb: 1 }}
            >
              Suggest a Webinar Topic
            </Typography>
            <Typography sx={{ color: "#94a3b8", mb: 2 }}>
              Is there something specific you'd like us to cover?
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/contact")}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
              }}
            >
              Send Suggestion
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Webinars;
